import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTheme2027 } from "../../../styles/theme2027";

// RAT home 2027 loading stand-ins, kept light (theme only) so index.jsx can show them while the page's code loads.
// The page's own loading state uses the same pieces, so the stand-in, the skeleton and the page share one geometry.

// Header geometry, shared with the skeleton: the logo frame (90px) starts below the greeting row and overlaps the
// band's bottom; the club name and the name / email sit to its right.
export const LOGO_TOP = 44;
export const NAME_INDENT = 106;
export const IDENT_LEFT = 122;
export const TOP_H = LOGO_TOP + 90;
// Phil 2026-10-08: tight under the logo; the body starts 6px below the logo frame.
export const BODY_PAD_TOP = 6;
// Phil 2026-10-08: the club name sits as close to the band's edge as the organizer name sits below it (both ~11px of ink).
export const BAND_PAD_BOTTOM = 5;
const BAND_H = 12 + 28 + 4 + 23 + BAND_PAD_BOTTOM;

// The loaded header's height per device (setup strip, Live now row and all), so the next load's skeleton takes the
// same space and nothing below it moves when the header lands (Phil 2026-10-08).
const heightKey = (account, sport) => `sh27RatHeaderH:${account || ""}:${sport || ""}`;
// No saved height yet: the header with a one-row setup strip (319 at 390), between a new club (399) and a finished one (201).
const BARE_H = 201;
const FIRST_VISIT_H = 319;

// The page's first render has no account yet (the store gets it in an effect): read it the way index.jsx's stand-in
// does, or the skeleton flips to the default height for a frame and back.
const accountGuess = () => {
  try {
    return new URLSearchParams(window.location.search).get("account") || (JSON.parse(localStorage.getItem("organizer")) || {}).accountid || "";
  } catch (e) {
    return "";
  }
};

// Same frame as the loaded header; its height is the last loaded header's on this device.
export const skeletonHeaderHeight = (account, sport) => {
  try {
    return Number(localStorage.getItem(heightKey(account || accountGuess(), sport))) || FIRST_VISIT_H;
  } catch (e) {
    return FIRST_VISIT_H;
  }
};

// `height` pins the stand-in to a given height (the reveal's cross-fade copy).
export function RatClubHeaderSkeleton({ account, sport, height: pinned }) {
  const t = useTheme2027();
  const height = pinned || skeletonHeaderHeight(account, sport);
  // Anything past the bare header is the setup strip and / or the Live now row: one block in its place.
  const fill = height - BARE_H - 12;
  const bar = (w, h = 12) => <span className="sh27-skel" style={{ display: "block", width: w, height: h, borderRadius: h / 2, background: t.track }} />;
  return (
    <section aria-busy="true" style={{ background: t.card, boxShadow: t.cardShadow, borderRadius: t.radius.card, overflow: "hidden", minHeight: height }}>
      <div style={{ position: "relative", minHeight: TOP_H }}>
        <div className="sh27-skel" style={{ height: BAND_H, background: t.subtle }} />
        <span className="sh27-skel" style={{ position: "absolute", left: 26, top: LOGO_TOP + 8, width: 74, height: 74, borderRadius: 19, background: t.track, boxShadow: `0 0 0 3px ${t.card}` }} />
        <div style={{ padding: `10px 18px 0 ${IDENT_LEFT}px`, display: "flex", flexDirection: "column", gap: 8 }}>
          {bar(110)}
          {bar(150, 11)}
        </div>
      </div>
      <div style={{ padding: `${BODY_PAD_TOP}px 18px 16px`, display: "flex", flexDirection: "column", gap: 12 }}>
        {fill > 20 && <span className="sh27-skel" style={{ display: "block", height: fill, borderRadius: 14, background: t.subtle }} />}
        <div style={{ height: 1, background: t.line, margin: "0 -18px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 44, margin: "-6px 0" }}>
          <span style={{ flex: "1 1 0" }}>{bar(140)}</span>
          <span className="sh27-skel" style={{ width: 74, height: 32, borderRadius: 10, background: t.track }} />
        </div>
      </div>
    </section>
  );
}


export function useRememberHeaderHeight(ref, account, sport) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      try {
        localStorage.setItem(heightKey(account, sport), String(Math.round(el.getBoundingClientRect().height)));
      } catch (e) {
        /* storage full or blocked */
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, account, sport]);
}

// Loading stand-ins at the real heights (Create card 100, section bar 56), so the page lands without moving.
export function RatHomeLoading({ part = "create" }) {
  const t = useTheme2027();
  const bar = part === "bar";
  return (
    <span
      aria-busy="true"
      className="sh27-skel"
      style={{ display: "block", height: bar ? 56 : 100, borderRadius: bar ? 16 : 20, background: t.card, boxShadow: t.cardShadow }}
    />
  );
}

export function SectionGhostRows({ rows = 4 }) {
  const t = useTheme2027();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 6 }}>
      <span style={{ height: 40, borderRadius: 12, background: t.subtle, display: "block" }} />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, height: 76, padding: 12, borderRadius: 16, background: t.subtle, boxSizing: "border-box" }}>
          <span style={{ flex: "0 0 42px", height: 46, borderRadius: 12, background: t.track }} />
          <span style={{ flex: "1 1 0", display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ display: "block", width: `${[70, 58, 64, 52][i % 4]}%`, height: 11, borderRadius: 6, background: t.track }} />
            <span style={{ display: "block", width: `${[40, 28, 34, 22][i % 4]}%`, height: 9, borderRadius: 6, background: t.track }} />
          </span>
          <span style={{ flex: "0 0 42px", height: 42, borderRadius: "50%", border: `4.5px solid ${t.track}`, boxSizing: "border-box" }} />
        </div>
      ))}
    </div>
  );
}

// Loading C (SCO-2785): stand-ins measured to the built 1.4 C row (166px with a one-row tray) and the list's controls, so
// rows land in place. `controls` adds the Upcoming / Past toggle, search row and range chips (the page's first load).
export function RowSkeletonC() {
  const t = useTheme2027();
  const sk = (w, h, r = h / 2, extra) => <span className="sh27-skel" style={{ display: "block", flex: "none", width: w, height: h, borderRadius: r, background: t.track, ...extra }} />;
  return (
    <div aria-hidden="true" style={{ height: 166, boxSizing: "border-box", borderRadius: 18, border: `1px solid ${t.line}`, background: t.card, boxShadow: t.cardShadow, overflow: "hidden", display: "flex", flexDirection: "column", padding: "12px 12px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 44, marginBottom: 8 }}>
        {sk(44, 44, 13)}
        <span style={{ flex: "1 1 0", display: "flex", flexDirection: "column", gap: 6 }}>
          {sk(96, 12)}
          {sk(120, 10)}
        </span>
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
          {sk(44, 20, 8)}
          {sk(22, 8)}
        </span>
      </div>
      <div style={{ height: 22, display: "flex", alignItems: "center" }}>{sk("62%", 17, 8)}</div>
      <div style={{ height: 19, display: "flex", alignItems: "center" }}>{sk("68%", 12)}</div>
      <div style={{ height: 19, display: "flex", alignItems: "center" }}>{sk("76%", 12)}</div>
      <div style={{ marginTop: "auto", margin: "auto -12px 0", height: 34, boxSizing: "border-box", padding: "0 12px", display: "flex", alignItems: "center", gap: 4, background: t.subtle, borderTop: `1px solid ${t.line}` }}>
        {sk(112, 20, 6)}
        {sk(72, 20, 6)}
      </div>
    </div>
  );
}

// The list's View choices (SCO-2784), saved per device for this account + sport; the skeleton reads them too.
export const viewPrefsKey = (account, sport) => `sh27RatView:${account || ""}:${sport || ""}`;
const readView = (account, sport) => {
  try {
    return JSON.parse(localStorage.getItem(viewPrefsKey(account || accountGuess(), sport))) || {};
  } catch (e) {
    return {};
  }
};
// Row heights per layout, measured on the built rows (one-line names, one-row trays).
const ROW_H = { cards: 166, compact: 58, grid: 199, showcase: 413, agenda: 58 };
const ROW_GAP = { cards: 10, compact: 6, grid: 10, showcase: 14, agenda: 6 };
// The calendar panel: week strip, or the month's rows of 40px days.
const calHeight = (mode) => {
  if (mode !== "month") return CAL_WEEK_H;
  const m = new Date();
  const first = new Date(m.getFullYear(), m.getMonth(), 1).getDay();
  const days = new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate();
  const weeks = Math.ceil((first + days) / 7);
  return CAL_MONTH_BASE + weeks * 40 + (weeks - 1) * 4;
};
const CAL_WEEK_H = 138;
const CAL_MONTH_BASE = 92;

function RowStandIn({ layout, t }) {
  if (layout === "cards") return <RowSkeletonC />;
  const sk = (w, h, r = h / 2) => <span className="sh27-skel" style={{ display: "block", flex: "none", width: w, height: h, borderRadius: r, background: t.track }} />;
  const card = { height: ROW_H[layout], boxSizing: "border-box", borderRadius: layout === "compact" || layout === "agenda" ? 14 : 18, border: `1px solid ${t.line}`, background: t.card, boxShadow: t.cardShadow, overflow: "hidden" };
  if (layout === "grid" || layout === "showcase") {
    return (
      <div aria-hidden="true" style={{ ...card, display: "flex", flexDirection: "column", gap: 8, padding: layout === "grid" ? 10 : 0 }}>
        {layout === "showcase" && <span className="sh27-skel" style={{ display: "block", height: 118, background: t.subtle }} />}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: layout === "showcase" ? "0 12px" : 0 }}>
          {sk(layout === "grid" ? 38 : 52, layout === "grid" ? 38 : 52, 12)}
          {sk("70%", 14, 7)}
          {sk("50%", 11)}
          {sk("60%", 11)}
        </div>
      </div>
    );
  }
  return (
    <div aria-hidden="true" style={{ ...card, display: "flex", alignItems: "center", gap: 10, padding: "8px 10px 8px 8px" }}>
      {layout === "compact" && sk(36, 36, 11)}
      <span style={{ flex: "1 1 0", display: "flex", flexDirection: "column", gap: 6 }}>
        {sk("62%", 13, 6)}
        {sk("78%", 10)}
      </span>
      {sk(34, 16, 6)}
    </div>
  );
}

export function ListSkeleton({ controls = true, rows, layout, account, sport }) {
  const t = useTheme2027();
  const view = readView(account, sport);
  const lay = layout || (["cards", "compact", "grid", "showcase", "agenda"].includes(view.layout) ? view.layout : "cards");
  const n = rows || (lay === "compact" || lay === "agenda" ? 6 : lay === "grid" ? 4 : lay === "showcase" ? 2 : 3);
  const block = (h, r, extra, key) => <span key={key} className="sh27-skel" style={{ display: "block", height: h, borderRadius: r, background: t.subtle, ...extra }} />;
  const item = (i) => {
    if (lay !== "agenda") return <RowStandIn key={i} layout={lay} t={t} />;
    return (
      <div key={i} style={{ display: "flex", gap: 8 }}>
        <span style={{ flex: "0 0 40px" }} />
        <span style={{ flex: "0 0 12px", display: "flex", justifyContent: "center" }}>
          <span style={{ width: 2, background: t.line }} />
        </span>
        <span style={{ flex: "1 1 0", minWidth: 0 }}>
          <RowStandIn layout="agenda" t={t} />
        </span>
      </div>
    );
  };
  return (
    <div aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 10, padding: controls ? "8px 0 10px" : 0 }}>
      {controls && (
        <>
          <div style={{ margin: "0 6px" }}>{block(40, 12)}</div>
          <div style={{ margin: "0 6px", display: "flex", gap: 8 }}>
            {block(42, 12, { flex: "1 1 0" })}
            {[0, 1, 2, 3].map((k) => block(42, 12, { flex: "0 0 42px" }, k))}
          </div>
          <div style={{ margin: "0 6px", display: "flex", gap: 6 }}>
            {[44, 62, 84, 86].map((w) => block(32, 10, { width: w, flex: "none" }, w))}
          </div>
          {view.cal && <div style={{ margin: "0 4px" }}>{block(calHeight(view.calMode), 16)}</div>}
        </>
      )}
      {view.group !== "none" && (
        <div style={{ height: 28, margin: "4px -4px 0", padding: "0 12px", display: "flex", alignItems: "center", background: t.subtle }}>
          <span className="sh27-skel" style={{ display: "block", width: 70, height: 10, borderRadius: 5, background: t.track }} />
        </div>
      )}
      {lay === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ROW_GAP.grid }}>{Array.from({ length: n }, (_, i) => item(i))}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: ROW_GAP[lay] }}>{Array.from({ length: n }, (_, i) => item(i))}</div>
      )}
    </div>
  );
}

// The section bar's stand-in at the top of the section card: the same 56px track, flush with the card's edges.
export function SectionBarStandIn() {
  const t = useTheme2027();
  return (
    <span
      aria-busy="true"
      className="sh27-skel"
      style={{ display: "block", height: 56, margin: "-4px -4px 4px", borderRadius: `${t.radius.card}px ${t.radius.card}px 0 0`, background: t.subtle, borderBottom: `1px solid ${t.line}`, boxSizing: "border-box" }}
    />
  );
}

// The whole page while loading: the same wrappers and gaps as Organizer.jsx's RAT home column.
export function RatPageSkeleton({ account, sport, headerHeight }) {
  const t = useTheme2027();
  return (
    <>
      <div style={{ width: "calc(100% - 32px)", margin: "0 auto", minWidth: 0 }}>
        <RatClubHeaderSkeleton account={account} sport={sport} height={headerHeight} />
      </div>
      <div style={{ width: "calc(100% - 32px)", margin: "12px auto", minWidth: 0 }}>
        <RatHomeLoading />
      </div>
      <div style={{ width: "calc(100% - 32px)", margin: "0 auto 20px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ background: t.card, boxShadow: t.cardShadow, borderRadius: t.radius.card, padding: 4, minWidth: 0 }}>
          <SectionBarStandIn />
          <ListSkeleton account={account} sport={sport} />
        </div>
      </div>
    </>
  );
}

// The reveal (Phil 2026-10-08, "go with 1"): when the real header lands at a different height than the stand-in showed
// (a device's first visit, or the header changed since the last one), the stand-ins stay on top at the height they
// had and fade out while the page fades in, so nothing slides. Same height: the copy is dropped before it paints.
// Place it first inside the page column's positioned wrapper; `headerRef` is the real header's slot.
export function RevealCrossFade({ ready, account, sport, headerRef }) {
  const shownH = useRef(null);
  const wasReady = useRef(ready);
  const [copyH, setCopyH] = useState(null);
  const copyRef = useRef(null);
  if (!ready) shownH.current = skeletonHeaderHeight(account, sport);
  if (ready && !wasReady.current) {
    wasReady.current = true;
    if (shownH.current && copyH === null) setCopyH(shownH.current);
  }
  useLayoutEffect(() => {
    if (copyH === null || !copyRef.current) return undefined;
    const real = headerRef.current ? Math.round(headerRef.current.getBoundingClientRect().height) : copyH;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (Math.abs(real - copyH) < 2 || still || !copyRef.current.animate) {
      setCopyH(0);
      return undefined;
    }
    const a = copyRef.current.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 450, easing: "cubic-bezier(.2,.8,.2,1)", fill: "forwards" });
    a.finished.then(() => setCopyH(0)).catch(() => {});
    return () => a.cancel();
  }, [copyH]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!copyH) return null;
  return (
    <div ref={copyRef} aria-hidden="true" data-reveal-copy="" style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 5, pointerEvents: "none" }}>
      <RatPageSkeleton account={account} sport={sport} headerHeight={copyH} />
    </div>
  );
}
